import { useTranslation } from 'react-i18next';
import { Link2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@ooh/ui/components/card';
import { Separator } from '@ooh/ui/components/separator';
import { type BundleDetail, type BundleMember } from '@ooh/contracts';
import { Link } from '../../plugs/link';
import type { DetailViewUnit } from './detail-view-unit';
import { PriceBreakdownCard } from './price-breakdown-card';
import {
  hasCostSection,
  hasSupplierSection,
  hasNotesSection,
  SpecsCard,
  SupplierCard,
  LocationCard,
} from './detail-sidebar-cards';
import { CostsCard, NotesCard } from './detail-sidebar-cost-cards';

// ─── Bundle ───────────────────────────────────────────────────

function BundleMemberLink({
  member,
  catalogSearch,
}: {
  member: BundleMember;
  /** Forwarded so the partner unit's own back-to-catalog link keeps the filters. CU-86cb1nq9n. */
  catalogSearch?: string;
}) {
  return (
    <Link
      to={`/media-units/${member.mediaUnitId}`}
      search={catalogSearch != null ? { catalogSearch } : undefined}
      className="flex items-center justify-between rounded-lg border border-border/60 p-3 hover:bg-muted/30 transition-colors"
      data-testid="bundle-partner-link"
    >
      <div>
        <p className="text-[13px] font-mono font-semibold">{member.supplierNumber ?? '\u2014'}</p>
        <p className="text-xs text-muted-foreground">{member.address ?? '\u2014'}</p>
      </div>
    </Link>
  );
}

export function BundleCard({
  bundle,
  catalogSearch,
}: {
  bundle: BundleDetail;
  catalogSearch?: string;
}) {
  const { t } = useTranslation('inventory');
  return (
    <Card data-testid="bundle-indicator">
      <CardHeader className="pb-2">
        <CardTitle className="text-[13px] uppercase tracking-wide text-muted-foreground font-semibold flex items-center gap-2">
          <Link2 className="h-3.5 w-3.5 text-primary" />
          {t('detail.bundle.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <p className="text-xs text-muted-foreground mb-3">{t('detail.bundle.description')}</p>
        <Separator className="mb-3" />
        <div className="space-y-2">
          {bundle.members.map((member) => (
            <BundleMemberLink
              key={member.mediaUnitId}
              member={member}
              catalogSearch={catalogSearch}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Sidebar ────────────────────────────────────────────────────
//
// Presence is keyed on KEY presence, not value: the client payload omits
// internal-only sections entirely (key absent), whereas the full internal
// payload always carries the key (value may be null). This keeps the internal
// catalog behavior unchanged (e.g. notes-card still shows its "no notes"
// placeholder when notes is null) while omitting the section for clients.
// The individual cards (SpecsCard, SupplierCard, LocationCard, CostsCard,
// NotesCard) live in `detail-sidebar-cards.tsx`, extracted purely to stay
// within this file's line budgets — no behavior change.

export function DetailSidebar({
  unit,
  // Accepted for call-site compatibility — no longer gates section presence
  // (field-visibility masks per field instead of removing the costs section).
  canViewCosts: _canViewCosts,
  catalogSearch,
  clientView = false,
}: {
  unit: DetailViewUnit;
  canViewCosts: boolean;
  /**
   * Opaque, serialized catalog filter/sort state forwarded to the bundle
   * partner unit link so its own back-to-catalog link keeps the previous
   * filters. Optional so the client detail route (no catalog context) is
   * unaffected. See `catalog-return-context.ts`. CU-86cb1nq9n.
   */
  catalogSearch?: string;
  /** Suppresses the Lokalizacja panel's Google Maps button — see MediaUnitDetailView's `clientView` doc. */
  clientView?: boolean;
}) {
  const { t } = useTranslation('inventory');
  const { t: tCommon } = useTranslation();
  const { t: tMap } = useTranslation('map');
  const bundles = unit.bundles ?? [];
  const maskedExplanation = t('table.maskedExplanation');
  return (
    <div className="space-y-4">
      {/* ─── Price / Status ───────────────────────────────── */}
      <PriceBreakdownCard unit={unit} />

      {/* ─── Technical specification ──────────────────────── */}
      <SpecsCard unit={unit} t={t} tCommon={tCommon} tMap={tMap} />

      {/* ─── Supplier (internal-only — omitted when absent) ── */}
      {hasSupplierSection(unit) ? (
        <SupplierCard unit={unit} t={t} maskedExplanation={maskedExplanation} />
      ) : null}

      {/* ─── Location (client-visible — present in both payloads) ── */}
      <LocationCard unit={unit} t={t} clientView={clientView} />

      {/* ─── Collapsible Costs (section-gated only — cells mask per row) ─── */}
      {hasCostSection(unit) ? (
        <CostsCard unit={unit} t={t} maskedExplanation={maskedExplanation} />
      ) : null}

      {/* ─── Bundle cards (internal-only) ──────────────────── */}
      {bundles.map((bundle) => (
        <BundleCard key={bundle.id} bundle={bundle} catalogSearch={catalogSearch} />
      ))}

      {/* ─── Notes (internal-only — omitted when absent) ───── */}
      {hasNotesSection(unit) ? <NotesCard unit={unit} t={t} /> : null}
    </div>
  );
}
